import { useState, useEffect } from "react";
import { openWithCacheBuster } from "./cacheBuster";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Copy, ExternalLink, FileText, CheckCircle2 } from "lucide-react";
import { CharacterCountBar } from "./CharacterCountBar";
import type {
  PageIssue,
  DuplicateTitleDetails,
  TitleMetaFix,
  ProposedFix,
} from "@/types/technicalAudit";

// Side-by-side resolution view for duplicate-title issues (task #34). A
// duplicate-title group is N page_issue rows that all share one <title>. Each
// row only writes ITS OWN page, so the specialist needs to see every sibling at
// once to decide how to differentiate THIS page. This card lays the whole group
// out side-by-side: the current page is highlighted with an editable proposed
// title + character meter, the siblings are shown read-only with their
// distinguishing slug surfaced so the differentiator is obvious at a glance.
//
// It emits the same canonical onEdit({ new_seo_title }) (plus the legacy
// new_title alias and target_field:"seo_title") contract as SeoMetaEditCard so
// the drawer's Approve/Apply path is unchanged. This is a SEO/meta title tag
// fix (Yoast/RankMath) — never the visible page title or H1.

const TITLE_LIMITS = { min: 30, max: 60, optimal: { min: 50, max: 60 } };

interface DuplicateTitleComparisonProps {
  issue: PageIssue;
  onEdit: (value: ProposedFix) => void;
}

// Pull the last meaningful path segment so siblings that differ only by
// location/service (e.g. ".../bathroom-remodel-troy-oh/") read clearly.
function distinguishingSlug(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/\/+$/, "");
    const seg = path.split("/").filter(Boolean).pop();
    return seg ? decodeURIComponent(seg) : "home";
  } catch {
    return url;
  }
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
}

function normalizedUrlKey(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    const path = parsed.pathname.replace(/\/+$/, "") || "/";
    return `${host}${path}`;
  } catch {
    return url
      .replace(/^https?:\/\/(www\.)?/i, "")
      .replace(/\/+$/, "")
      .toLowerCase();
  }
}

function isSameUrl(a: string, b: string): boolean {
  return normalizedUrlKey(a) === normalizedUrlKey(b);
}

export function DuplicateTitleComparison({
  issue,
  onEdit,
}: DuplicateTitleComparisonProps) {
  const details = (issue.issue_details || {}) as DuplicateTitleDetails;
  const rawDetails = details as DuplicateTitleDetails & Record<string, unknown>;
  const sharedTitle =
    details.title ||
    (rawDetails.duplicate_title as string) ||
    issue.page_title ||
    "";
  const urls = details.duplicate_urls || [];
  const currentUrl = issue.page_url;
  const urlsWithCurrent = urls.some((url) => isSameUrl(url, currentUrl))
    ? urls
    : [currentUrl, ...urls];

  // Order the current page first so it anchors the comparison.
  const groupedUrls = [
    ...urlsWithCurrent.filter((url) => isSameUrl(url, currentUrl)),
    ...urlsWithCurrent.filter((url) => !isSameUrl(url, currentUrl)),
  ];
  const orderedUrls = groupedUrls.length ? groupedUrls : [currentUrl];
  const hasKnownSiblings = orderedUrls.some(
    (url) => !isSameUrl(url, currentUrl),
  );
  const displayedPageCount = orderedUrls.length;
  const pageNoun = displayedPageCount === 1 ? "page" : "pages";

  const proposed = issue.proposed_fix as
    | (TitleMetaFix & { reasoning?: string; new_title?: string })
    | null;
  const approved = issue.approved_value as
    | (TitleMetaFix & { reasoning?: string; new_title?: string })
    | null;
  const draftKey = `${issue.id}:${issue.issue_type}:${JSON.stringify(issue.approved_value ?? issue.proposed_fix ?? "")}`;
  const initialTitle =
    approved?.new_seo_title ||
    approved?.new_title ||
    proposed?.new_seo_title ||
    proposed?.new_title ||
    "";
  const [draft, setDraft] = useState<{ key: string; title: string } | null>(
    null,
  );
  const editedTitle = draft?.key === draftKey ? draft.title : initialTitle;
  const reasoning = approved?.reasoning || proposed?.reasoning;

  // Push the draft up so Approve/Apply send exactly what the specialist sees.
  useEffect(() => {
    const value: TitleMetaFix = {};
    const t = editedTitle.trim() || undefined;
    value.new_seo_title = t;
    value.new_title = t;
    value.target_field = "seo_title";
    onEdit(value as ProposedFix);
  }, [editedTitle, onEdit]);

  const hasProposed = Boolean(
    approved?.new_seo_title ||
      approved?.new_title ||
      proposed?.new_seo_title ||
      proposed?.new_title,
  );

  return (
    <div className="space-y-4">
      {/* Shared title conflict banner */}
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
              <Copy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            {hasKnownSiblings
              ? `Same SEO title tag on ${displayedPageCount} ${pageNoun}`
              : "Duplicate SEO title tag detected"}
            <Badge
              variant="outline"
              className={
                hasKnownSiblings
                  ? "ml-auto bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                  : "ml-auto bg-muted text-foreground border-border"
              }
            >
              {hasKnownSiblings ? "Conflict" : "Review"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <blockquote className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-4 rounded-r-lg">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-300 italic">
              &ldquo;{sharedTitle}&rdquo;
            </p>
          </blockquote>
          <p className="mt-2 text-xs text-muted-foreground">
            {hasKnownSiblings ? (
              <>
                Give each page its own SEO title tag (Yoast/RankMath) — not the
                visible page title or H1. You're editing{" "}
                <span className="font-medium text-foreground">this page</span>{" "}
                below — open the siblings to differentiate them too.
              </>
            ) : (
              <>
                This issue did not include sibling URLs. Give{" "}
                <span className="font-medium text-foreground">this page</span> a
                unique SEO title tag, then use a follow-up crawl to confirm the
                duplicate group is resolved.
              </>
            )}
          </p>
        </CardContent>
      </Card>

      {/* Side-by-side comparison grid */}
      <div
        className={
          hasKnownSiblings
            ? "grid grid-cols-1 gap-3 lg:grid-cols-2"
            : "grid grid-cols-1 gap-3"
        }
      >
        {orderedUrls.map((url) => {
          const isCurrent = isSameUrl(url, currentUrl);
          return (
            <Card
              key={url}
              className={
                isCurrent
                  ? "border-teal-dark/40 bg-teal-light/40 shadow-sm"
                  : "border-border shadow-sm"
              }
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-xs font-semibold">
                  {isCurrent ? (
                    <Badge className="bg-teal text-white hover:bg-teal-hover">
                      This page
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-muted text-muted-foreground border-border"
                    >
                      Sibling
                    </Badge>
                  )}
                  <span className="font-mono text-foreground truncate">
                    /{distinguishingSlug(url)}
                  </span>
                  <a
                    href={url}
                    onClick={(e) => openWithCacheBuster(e, url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-muted-foreground hover:text-muted-foreground"
                    title="Open page"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <p className="text-[11px] font-mono break-all text-muted-foreground">
                  {pathOf(url)}
                </p>

                {/* Current title (the shared / duplicated one) */}
                <div>
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Current SEO title tag
                  </div>
                  <div className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">
                    {sharedTitle}
                  </div>
                </div>

                {isCurrent ? (
                  <div>
                    <div className="mb-1 flex items-center gap-1 text-xs font-medium text-teal-dark">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      New SEO title tag (editable)
                    </div>
                    {hasProposed || editedTitle ? (
                      <>
                        <Textarea
                          value={editedTitle}
                          onChange={(e) =>
                            setDraft({ key: draftKey, title: e.target.value })
                          }
                          className="min-h-[64px] text-sm"
                          placeholder="Write a unique SEO title tag for this page..."
                        />
                        <div className="mt-2">
                          <CharacterCountBar
                            current={editedTitle.trim().length}
                            min={TITLE_LIMITS.min}
                            max={TITLE_LIMITS.max}
                            optimal={TITLE_LIMITS.optimal}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="rounded-md border border-dashed bg-card p-3 text-xs text-muted-foreground">
                        Click <span className="font-medium">Generate fix</span>{" "}
                        above to draft a unique title, then edit it here.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40/40 p-2 text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    Still shares the duplicate title — resolve this page from
                    its own audit row.
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI reasoning for this page's suggestion */}
      {reasoning && (
        <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 p-3">
          <p className="text-xs font-medium text-blue-800 dark:text-blue-300">AI Reasoning:</p>
          <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">{reasoning}</p>
        </div>
      )}
    </div>
  );
}
